
import { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DirectoryFilters as DirectoryFiltersType } from '@/pages/Directory';

interface DirectoryFiltersProps {
  filters: DirectoryFiltersType;
  onFilterChange: (filters: Partial<DirectoryFiltersType>) => void;
  onClearFilters: () => void;
}

export const DirectoryFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters 
}: DirectoryFiltersProps) => {
  const [localSearch, setLocalSearch] = useState(filters.search);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange({ search: localSearch });
  };

  const hasActiveFilters = filters.search || filters.productType || filters.manufacturer;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Filters
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="ml-auto h-auto p-1"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="space-y-2">
          <label className="text-sm font-medium">Search Products</label>
          <div className="flex gap-2">
            <Input
              placeholder="Search by name or description..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
            <Button type="submit" size="sm">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </form>

        {/* Product Type */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Product Type</label>
          <Select
            value={filters.productType}
            onValueChange={(value) => onFilterChange({ productType: value === 'all' ? '' : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="cleaning_product">Cleaning Product</SelectItem>
              <SelectItem value="paper_product">Paper Product</SelectItem>
              <SelectItem value="laundry_product">Laundry Product</SelectItem>
              <SelectItem value="formulated">Formulated</SelectItem>
              <SelectItem value="raw_material">Raw Material</SelectItem>
              <SelectItem value="packaging">Packaging</SelectItem>
              <SelectItem value="equipment">Equipment</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Manufacturer */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Manufacturer</label>
          <Input
            placeholder="Enter manufacturer name..."
            value={filters.manufacturer}
            onChange={(e) => onFilterChange({ manufacturer: e.target.value })}
          />
        </div>
      </CardContent>
    </Card>
  );
};
